(function () {
  'use strict';

  const gsap = window.gsap;
  const { subEvents, PUB_SUB_EVENTS, MobileMenu, Search, animProp, easePower4, prefersReducedMotion } = window.Universe;

  class Navigation {
    constructor() {
      this.init();
    }

    setup() {
      this.glassOnScroll = this.DOM.headerNav.getAttribute('data-glass-scroll') !== null;
      this.blurRadius = this.DOM.headerNav.getAttribute('data-blur-radius') || 0;
      this.glassAlways = this.DOM.headerNav.getAttribute('data-glass-always') !== null;

      this.setSubmenus();
      this.setExploreSub();
      this.setCollectionsSub();
      this.setShopSublistToggles();
      this.setSearch();
      this.setMenu();
      this.setMobileMenu();
      this.setGlassAnimation();
      this.setScrollAnimation();
      this.setHeightVar();
      this.setDetailsHover();
      this.setMenuClickExclusive();
      this.setClickOutsideClose();
    }

    reinit() {
      this.destroy();
      this.init();
    }

    reinitSection(sectionEl) {
      if (sectionEl.querySelector('.js-header-nav')) {
        this.reinit();
      }
    }

    destroySection(sectionEl) {
      if (sectionEl.querySelector('.js-header-nav')) {
        this.destroy();
      }
    }

    setSubmenus() {
      if (this.DOM.submenus.length) {
        this.ctx.add(() => {
          this.DOM.submenus.forEach((submenu) => {
            submenu.addEventListener('keydown', this.onSubmenuKeyDown);
          });
          return () => {
            this.DOM.submenus.forEach((submenu) => {
              submenu.removeEventListener('keydown', this.onSubmenuKeyDown);
            });
          };
        });
      }

      if (this.DOM.classicSubmenus.length) {
        this.ctx.add(() => {
          this.DOM.classicSubmenus.forEach((submenu) => {
            submenu.addEventListener('keydown', this.onSubmenuKeyDown);
          });
          return () => {
            this.DOM.classicSubmenus.forEach((submenu) => {
              submenu.removeEventListener('keydown', this.onSubmenuKeyDown);
            });
          };
        });
      }

      if (this.DOM.headerNavSubActivators.length) {
        this.ctx.add(() => {
          const clickActivators = [];

          const onActivatorClick = (e) => {
            e.preventDefault();
            const activator = e.currentTarget;
            const relativeSubmenuId = activator.dataset.submenuId;
            if (this.activeSubmenu === relativeSubmenuId) {
              this.closeActiveSubmenu();
            } else {
              this.onSubmenuEnter(e);
            }
          };

          for (let i = 0; i < this.DOM.headerNavSubActivators.length; i++) {
            const activator = this.DOM.headerNavSubActivators[i];
            activator.addEventListener('keydown', this.onActivatorKeyDown);

            if (activator.dataset.openTrigger === 'click') {
              activator.addEventListener('click', onActivatorClick);
              clickActivators.push(activator);
            } else {
              activator.addEventListener('mouseenter', this.onSubmenuEnter);
            }
          }
          document.addEventListener('mousemove', this.onDocumentMouseMove);
          document.addEventListener('keydown', this.onGlobalKeyDown);
          document.addEventListener('focusin', this.onGlobalFocusIn);

          return () => {
            for (let i = 0; i < this.DOM.headerNavSubActivators.length; i++) {
              this.DOM.headerNavSubActivators[i].removeEventListener('mouseenter', this.onSubmenuEnter);
              this.DOM.headerNavSubActivators[i].removeEventListener('keydown', this.onActivatorKeyDown);
            }
            clickActivators.forEach((activator) => activator.removeEventListener('click', onActivatorClick));
            document.removeEventListener('mousemove', this.onDocumentMouseMove);
            document.removeEventListener('keydown', this.onGlobalKeyDown);
            document.removeEventListener('focusin', this.onGlobalFocusIn);
          };
        });

        if (this.DOM.headerOtherItems.length) {
          this.ctx.add(() => {
            for (let i = 0; i < this.DOM.headerOtherItems.length; i++) {
              this.DOM.headerOtherItems[i].addEventListener('mouseenter', this.onOtherSubLinkEnter);
            }
            return () => {
              for (let i = 0; i < this.DOM.headerOtherItems.length; i++) {
                this.DOM.headerOtherItems[i].removeEventListener('mouseenter', this.onOtherSubLinkEnter);
              }
            };
          });
        }
      }
    }

    setExploreSub() {
      if (this.DOM.submenuExploreLinks.length) {
        this.DOM.submenuExplore = document.querySelector('.js-header-nav-submenu[data-submenu="explore"]');

        this.ctx.add(() => {
          for (let i = 0; i < this.DOM.submenuExploreLinks.length; i++) {
            this.DOM.submenuExploreLinks[i].addEventListener('mouseenter', this.onSubExploreLinkEnter);
            this.DOM.submenuExploreLinks[i].addEventListener('mouseleave', this.onSubExploreLinkLeave);
          }
          return () => {
            for (let i = 0; i < this.DOM.submenuExploreLinks.length; i++) {
              this.DOM.submenuExploreLinks[i].removeEventListener('mouseenter', this.onSubExploreLinkEnter);
              this.DOM.submenuExploreLinks[i].removeEventListener('mouseleave', this.onSubExploreLinkLeave);
            }
          };
        });
      }
    }

    setSearch() {
      if (this.DOM.searchActivators.length && this.DOM.searchLayer) {
        this.DOM.searchContent = this.DOM.searchLayer.querySelector('.js-gsearch-content');
        this.DOM.searchInput = this.DOM.searchLayer.querySelector('.js-gsearch-input');

        this.ctx.add(() => {
          for (let i = 0; i < this.DOM.searchActivators.length; i++) {
            this.DOM.searchActivators[i].addEventListener('click', this.toggleSearch);
          }
          this.DOM.searchCloser.addEventListener('click', this.onCloseSearch);

          return () => {
            for (let i = 0; i < this.DOM.searchActivators.length; i++) {
              this.DOM.searchActivators[i].removeEventListener('click', this.toggleSearch);
            }
            this.DOM.searchCloser.removeEventListener('click', this.onCloseSearch);
          };
        });

        this.searchInstance = new Search(this.DOM.searchLayer);
      }
    }

    setCollectionsSub() {
      if (this.DOM.submenuCollections) {
        this.ctx.add(() => {
          for (let i = 0; i < this.DOM.submenuCollections.length; i++) {
            this.DOM.submenuCollections[i].addEventListener('mouseenter', this.onSubCollectionsEnter);
            this.DOM.submenuCollections[i].addEventListener('mouseleave', this.onSubCollectionsLeave);
          }
          return () => {
            for (let i = 0; i < this.DOM.submenuCollections.length; i++) {
              this.DOM.submenuCollections[i].removeEventListener('mouseenter', this.onSubCollectionsEnter);
              this.DOM.submenuCollections[i].removeEventListener('mouseleave', this.onSubCollectionsLeave);
            }
          };
        });
      }
    }

    setShopSublistToggles() {
      this.DOM.shopSublistToggles = document.querySelectorAll('.js-header-submenu-shop-toggle');
      this.DOM.shopSublists = document.querySelectorAll('.js-header-submenu-shop-sublist');

      if (this.DOM.shopSublistToggles.length) {
        this.ctx.add(() => {
          for (let i = 0; i < this.DOM.shopSublistToggles.length; i++) {
            this.DOM.shopSublistToggles[i].addEventListener('click', this.onShopSublistToggle);
          }
          return () => {
            for (let i = 0; i < this.DOM.shopSublistToggles.length; i++) {
              this.DOM.shopSublistToggles[i].removeEventListener('click', this.onShopSublistToggle);
            }
          };
        });
      }
    }

    onShopSublistToggle(e) {
      const activator = e.currentTarget;
      const sublistId = activator.dataset.sublistId;
      const sublist = document.querySelector('.js-header-submenu-shop-sublist[data-sublist-id="' + sublistId + '"]');

      if (!sublist) return;

      const isOpen = activator.getAttribute('aria-expanded') === 'true';

      if (isOpen) {
        activator.setAttribute('aria-expanded', 'false');
        sublist.setAttribute('aria-hidden', 'true');
        this.ctx.add(() => {
          gsap.to(sublist, { height: 0, autoAlpha: 0, duration: 0.4, ease: easePower4 });
        });
      } else {
        const parentGroup = activator.closest('.header-submenu__shop-group');
        if (parentGroup) {
          const openToggles = parentGroup.querySelectorAll('.js-header-submenu-shop-toggle[aria-expanded="true"]');
          openToggles.forEach((toggle) => {
            if (toggle !== activator) {
              const otherId = toggle.dataset.sublistId;
              const otherSublist = document.querySelector(
                '.js-header-submenu-shop-sublist[data-sublist-id="' + otherId + '"]'
              );
              toggle.setAttribute('aria-expanded', 'false');
              if (otherSublist) {
                otherSublist.setAttribute('aria-hidden', 'true');
                this.ctx.add(() => {
                  gsap.to(otherSublist, { height: 0, autoAlpha: 0, duration: 0.4, ease: easePower4 });
                });
              }
            }
          });
        }

        activator.setAttribute('aria-expanded', 'true');
        sublist.setAttribute('aria-hidden', 'false');
        this.ctx.add(() => {
          gsap.to(sublist, { height: 'auto', autoAlpha: 1, duration: 0.4, ease: easePower4 });
        });
      }
    }

    setMenu() {
      if (this.DOM.headerNavMenuToggle) {
        this.ctx.add(() => {
          this.DOM.headerNavMenuToggle.addEventListener('click', this.onMenuToggle);
          return () => { this.DOM.headerNavMenuToggle.removeEventListener('click', this.onMenuToggle); };
        });

        if (this.DOM.menuWrap) {
          this.DOM.menuInner = this.DOM.menuWrap.querySelector('.js-header-nav-menu-inner');
          this.DOM.menuSides = this.DOM.menuWrap.querySelectorAll('.js-header-nav-menu-side');
          this.DOM.menuClose = this.DOM.menuWrap.querySelector('.js-header-nav-menu-close');
          this.DOM.menuLinksHaveSide = this.DOM.menuWrap.querySelectorAll('.js-header-nav-menu-has-side');
          this.DOM.menuLinksHaveSub = this.DOM.menuWrap.querySelectorAll('.js-header-nav-menu-has-sub');
          this.DOM.menuLinksHaveSubsub = this.DOM.menuWrap.querySelectorAll('.js-header-nav-menu-has-subsub');
          this.DOM.menuSubmenus = this.DOM.menuWrap.querySelectorAll('.js-header-nav-menu-submenu');
          this.DOM.menuShopToggles = this.DOM.menuWrap.querySelectorAll('.js-header-menu-shop-toggle');
          this.DOM.menuShopSublists = this.DOM.menuWrap.querySelectorAll('.js-header-menu-shop-sublist');

          this.DOM.menuExploreWrap = this.DOM.menuWrap.querySelector('.js-header-nav-menu-side[data-side="explore"]');
          this.DOM.menuExploreLinks = this.DOM.menuWrap.querySelectorAll('.js-header-menu-explore-link');

          this.ctx.add(() => {
            if (this.DOM.menuClose) {
              this.DOM.menuClose.addEventListener('click', this.onCloseMenu);
            }

            for (let i = 0; i < this.DOM.menuLinksHaveSide.length; i++) {
              this.DOM.menuLinksHaveSide[i].addEventListener('click', this.onMenuLinkSideClick);
            }

            for (let i = 0; i < this.DOM.menuLinksHaveSub.length; i++) {
              this.DOM.menuLinksHaveSub[i].addEventListener('click', this.onMenuLinkSubClick);
            }
            for (let i = 0; i < this.DOM.menuLinksHaveSubsub.length; i++) {
              this.DOM.menuLinksHaveSubsub[i].addEventListener('click', this.onMenuSubLinkSubClick);
            }
            for (let i = 0; i < this.DOM.menuShopToggles.length; i++) {
              this.DOM.menuShopToggles[i].addEventListener('click', this.onMenuShopSublistToggle);
            }

            if (this.DOM.menuExploreLinks.length) {
              for (let i = 0; i < this.DOM.menuExploreLinks.length; i++) {
                this.DOM.menuExploreLinks[i].addEventListener('mouseenter', this.onMenuExploreLinkEnter);
                this.DOM.menuExploreLinks[i].addEventListener('mouseleave', this.onMenuExploreLinkLeave);
              }
            }

            document.addEventListener('keydown', this.onKeyDownMenu);

            gsap.set(this.DOM.menuSides, { xPercent: -100, opacity: 0, pointerEvents: 'none' });
            if (this.DOM.menuSubmenus.length) {
              gsap.set(this.DOM.menuSubmenus, {
                height: 0,
                opacity: 0,
                visibility: 'hidden',
              });
            }

            return () => {
              if (this.DOM.menuClose) {
                this.DOM.menuClose.removeEventListener('click', this.onCloseMenu);
              }

              for (let i = 0; i < this.DOM.menuLinksHaveSide.length; i++) {
                this.DOM.menuLinksHaveSide[i].removeEventListener('click', this.onMenuLinkSideClick);
              }

              for (let i = 0; i < this.DOM.menuLinksHaveSub.length; i++) {
                this.DOM.menuLinksHaveSub[i].removeEventListener('click', this.onMenuLinkSubClick);
              }

              for (let i = 0; i < this.DOM.menuLinksHaveSubsub.length; i++) {
                this.DOM.menuLinksHaveSubsub[i].removeEventListener('click', this.onMenuSubLinkSubClick);
              }

              if (this.DOM.menuShopToggles) {
                for (let i = 0; i < this.DOM.menuShopToggles.length; i++) {
                  this.DOM.menuShopToggles[i].removeEventListener('click', this.onMenuShopSublistToggle);
                }
              }

              if (this.DOM.menuExploreLinks) {
                for (let i = 0; i < this.DOM.menuExploreLinks.length; i++) {
                  this.DOM.menuExploreLinks[i].removeEventListener('mouseenter', this.onMenuExploreLinkEnter);
                  this.DOM.menuExploreLinks[i].removeEventListener('mouseleave', this.onMenuExploreLinkLeave);
                }
              }

              document.removeEventListener('keydown', this.onKeyDownMenu);
            };
          });

          this.closeMenu(true);
        }
      }
    }

    setMobileMenu() {
      if (this.DOM.mobileToggles.length) {
        this.mobileMenuUpdateUnsubscriber = subEvents.subscribe(PUB_SUB_EVENTS.mobileMenuOpen, this.onMobileMenuOpen);
        this.mobileMenu = new MobileMenu(this.DOM.mobileToggles);
      }
    }

    setGlassAnimation() {
      const isSticky = this.DOM.headerNav.classList.contains('is-sticky');

      // Always-on glass is handled in CSS (data-glass-always), no scroll observer needed.
      if (this.glassOnScroll && isSticky && !this.glassAlways) {
        this.DOM.headerNav.style.setProperty('--header-blur-radius', this.blurRadius + 'px');

        this.glassElements = {
          headerMain: this.DOM.headerNav.querySelectorAll('.header-nav__main'),
          headerBottom: this.DOM.headerNav.querySelector('.header-nav__bottom'),
        };

        this.setupGlassObserver();
      }
    }

    setScrollAnimation() {
      const isSticky = this.DOM.headerNav.classList.contains('is-sticky');

      if (this.isTransparent && isSticky && !this.glassOnScroll) {
        this.solidElements = {
          headerMainDesk: this.DOM.headerNav.querySelector('.header-nav__main--desktop'),
          headerMainMobile: this.DOM.headerNav.querySelector('.header-nav__main--mobile'),
          headerBottom: this.DOM.headerNav.querySelector('.header-nav__bottom'),
        };

        this.setupSolidObserver();
      }
    }

    setupGlassObserver() {
      this.glassSentinel = document.createElement('div');
      this.glassSentinel.style.cssText = 'position:absolute;top:100px;left:0;width:1px;height:1px;pointer-events:none;';
      document.body.appendChild(this.glassSentinel);

      this.glassObserver = new IntersectionObserver(
        (entries) => {
          const entry = entries[0];
          const shouldBeGlass = !entry.isIntersecting;
          if (this.glassElements.headerMain) {
            this.glassElements.headerMain.forEach((header) => {
              shouldBeGlass ? header.setAttribute('is-glass', 'true') : header.removeAttribute('is-glass');
            });
          }
          if (this.glassElements.headerBottom) {
            shouldBeGlass
              ? this.glassElements.headerBottom.setAttribute('is-glass', 'true')
              : this.glassElements.headerBottom.removeAttribute('is-glass');
          }
        },
        { threshold: 0 }
      );

      this.glassObserver.observe(this.glassSentinel);
    }

    setupSolidObserver() {
      this.solidSentinel = document.createElement('div');
      this.solidSentinel.style.cssText = 'position:absolute;top:60px;left:0;width:1px;height:1px;pointer-events:none;';
      document.body.appendChild(this.solidSentinel);

      this.solidObserver = new IntersectionObserver(
        (entries) => {
          const entry = entries[0];
          const shouldBeSolid = !entry.isIntersecting;
          const headerMainDesk = this.solidElements.headerMainDesk;
          const headerMainMobile = this.solidElements.headerMainMobile;
          const headerBottom = this.solidElements.headerBottom;

          if (shouldBeSolid) {
            if (headerMainDesk) headerMainDesk.setAttribute('is-solid', 'true');
            if (headerMainMobile) headerMainMobile.setAttribute('is-solid', 'true');
            if (headerBottom) headerBottom.setAttribute('is-solid', 'true');
          } else {
            if (headerMainDesk) headerMainDesk.removeAttribute('is-solid');
            if (headerMainMobile) headerMainMobile.removeAttribute('is-solid');
            if (headerBottom) headerBottom.removeAttribute('is-solid');
          }
        },
        { threshold: 0 }
      );

      this.solidObserver.observe(this.solidSentinel);
    }

    setHeightVar() {
      this.DOM.headerGroup = document.querySelector('.js-header-group');

      const startObserver = () => {
        this.headerResizeObserver = new ResizeObserver((entries) => {
          const entry = entries[0];
          const bbs = entry.borderBoxSize;
          const height = (bbs && bbs[0] && bbs[0].blockSize !== undefined) ? bbs[0].blockSize : entry.contentRect.height;
          document.documentElement.style.setProperty('--header-height', height + 'px');
        });
        this.headerResizeObserver.observe(this.DOM.headerGroup);
      };

      const interactionEvents = ['pointerdown', 'scroll', 'touchstart', 'keydown'];
      const onFirstInteraction = () => {
        interactionEvents.forEach((evt) => window.removeEventListener(evt, onFirstInteraction, { passive: true }));
        startObserver();
      };
      interactionEvents.forEach((evt) => window.addEventListener(evt, onFirstInteraction, { passive: true }));
    }

    setDetailsHover() {
      if (!window.matchMedia('(hover: hover)').matches) return;

      const detailsEls = this.DOM.headerNav.querySelectorAll('.header-nav__menu-details[data-open-trigger="hover"]');
      if (!detailsEls.length) return;

      this.ctx.add(() => {
        const handlers = [];
        const needsMouseMove = !this.DOM.headerNavSubActivators.length;
        if (needsMouseMove) {
          document.addEventListener('mousemove', this.onDocumentMouseMove);
        }

        detailsEls.forEach((details) => {
          const parent = details.parentElement;

          const onEnter = () => {
            if (this.activeDetails && this.activeDetails.contains(details)) {
              this.activeDetails.querySelectorAll('.header-nav__menu-details[open]').forEach((sibling) => {
                if (sibling !== details) sibling.open = false;
              });
              details.open = true;
            } else {
              this.closeActiveSubmenu();
              details.open = true;
              this.activeDetails = details;
            }

            const subsubmenu = details.querySelector(':scope > .header-nav__menu-subsubmenu');
            if (subsubmenu) this.flipSubsubmenu(subsubmenu);
          };

          parent.addEventListener('mouseenter', onEnter);
          handlers.push({ parent: parent, onEnter: onEnter });
        });

        return () => {
          if (needsMouseMove) {
            document.removeEventListener('mousemove', this.onDocumentMouseMove);
          }
          handlers.forEach((h) => {
            h.parent.removeEventListener('mouseenter', h.onEnter);
          });
        };
      });
    }

    setMenuClickExclusive() {
      const topDetailsEls = this.DOM.headerNav.querySelectorAll(
        '.header-nav__menu > .header-nav__menu-item > .header-nav__menu-details[data-open-trigger="click"]'
      );
      if (!topDetailsEls.length) return;

      this.ctx.add(() => {
        const onToggle = (e) => {
          const details = e.currentTarget;

          if (details.open) {
            // Closes sibling top-level dropdowns (and their nested submenus), plus any
            // active megamenu, so opening one classic dropdown closes every other menu.
            this.closeActiveSubmenu(details);
          } else {
            details.querySelectorAll('.header-nav__menu-details[open]').forEach((nested) => {
              nested.open = false;
            });
          }
        };

        topDetailsEls.forEach((details) => details.addEventListener('toggle', onToggle));

        return () => {
          topDetailsEls.forEach((details) => details.removeEventListener('toggle', onToggle));
        };
      });
    }

    setClickOutsideClose() {
      const hasClickTrigger =
        this.DOM.headerNav.querySelector('.header-nav__menu-details[data-open-trigger="click"]') ||
        this.DOM.headerNav.querySelector('.js-header-nav-submenu-activate[data-open-trigger="click"]');
      if (!hasClickTrigger) return;

      this.ctx.add(() => {
        const onDocumentClick = (e) => {
          const target = e.target;

          if (this.activeSubmenu) {
            const activator = document.querySelector(
              '.js-header-nav-submenu-activate[data-submenu-id="' + this.activeSubmenu + '"]'
            );
            if (activator && activator.dataset.openTrigger === 'click') {
              const panel = document.querySelector(
                '.js-header-nav-submenu[data-submenu-id="' + this.activeSubmenu + '"]'
              );
              const isInside = activator.contains(target) || (panel && panel.contains(target));
              if (!isInside) this.closeActiveSubmenu();
            }
          }

          this.DOM.headerNav
            .querySelectorAll(
              '.header-nav__menu > .header-nav__menu-item > .header-nav__menu-details[data-open-trigger="click"][open]'
            )
            .forEach((details) => {
              if (!details.contains(target)) details.open = false;
            });
        };

        document.addEventListener('click', onDocumentClick);

        return () => {
          document.removeEventListener('click', onDocumentClick);
        };
      });
    }

    destroyHeaderObservers() {
      if (this.headerResizeObserver) {
        this.headerResizeObserver.disconnect();
        this.headerResizeObserver = null;
        document.documentElement.style.removeProperty('--header-height');
      }

      if (this.glassObserver) {
        this.glassObserver.disconnect();
        if (this.glassSentinel) this.glassSentinel.remove();
        this.glassObserver = null;
        this.glassSentinel = null;
        this.glassElements = null;
      }

      if (this.solidObserver) {
        this.solidObserver.disconnect();
        if (this.solidSentinel) this.solidSentinel.remove();
        this.solidObserver = null;
        this.solidSentinel = null;
        this.solidElements = null;
      }

    }

    onMobileMenuOpen() {
      if (this.isOpenSearch) {
        this.closeSearch();
      }
    }

    onKeyDownMenu(e) {
      if (e.key === 'Escape' && this.isOpenMenu) {
        this.closeMenu();
        if (this.DOM.headerNavMenuToggle) {
          this.DOM.headerNavMenuToggle.focus();
        }
        return;
      }

      if (e.key === 'Tab' && this.isOpenMenu && this.DOM.menuInner) {
        const focusableElements = this.DOM.menuInner.querySelectorAll(
          'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        );

        const visibleFocusableElements = Array.from(focusableElements).filter((el) => {
          const style = window.getComputedStyle(el);
          return style.display !== 'none' && style.visibility !== 'hidden' && el.offsetParent !== null;
        });

        if (visibleFocusableElements.length === 0) return;

        const firstFocusable = visibleFocusableElements[0];
        const lastFocusable = visibleFocusableElements[visibleFocusableElements.length - 1];
        const activeEl = document.activeElement;

        if (e.shiftKey) {
          if (activeEl === firstFocusable) {
            e.preventDefault();
            lastFocusable.focus();
          }
        } else {
          if (activeEl === lastFocusable) {
            e.preventDefault();
            firstFocusable.focus();
          }
        }
      }
    }

    onKeyDownSearch(e) {
      if (e.key === 'Escape' && this.isOpenSearch) {
        this.closeSearch();
      }
    }

    onGlobalFocusIn(e) {
      if (this.activeSubmenu) {
        const target = e.target;
        const isInsideNav = this.DOM.headerNav.contains(target);
        const isInsideSubmenu = Array.from(this.DOM.submenus).some((submenu) => submenu.contains(target));

        if (!isInsideNav && !isInsideSubmenu) {
          this.closeActiveSubmenu();
        }
      }
    }

    onSubmenuKeyDown(e) {
      if (!this.activeSubmenu) {
        const focusedParent = document.querySelector('.header-nav__menu-item.is-focused');
        if (!focusedParent) return;
      }

      const activeEl = document.activeElement;
      const submenu = e.currentTarget;
      const focusableElements = submenu.querySelectorAll(
        'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      const visibleFocusableElements = Array.from(focusableElements).filter((el) => {
        const style = window.getComputedStyle(el);
        return style.display !== 'none' && style.visibility !== 'hidden' && el.offsetParent !== null;
      });

      if (visibleFocusableElements.length === 0) return;

      const firstFocusable = visibleFocusableElements[0];
      const lastFocusable = visibleFocusableElements[visibleFocusableElements.length - 1];

      if (e.key === 'Tab') {
        if (e.shiftKey) {
          if (activeEl === firstFocusable) {
            e.preventDefault();
            lastFocusable.focus();
          }
        } else {
          if (activeEl === lastFocusable) {
            e.preventDefault();
            firstFocusable.focus();
          }
        }
        e.stopPropagation();
      }

      if (e.key === 'Escape') {
        if (submenu.classList.contains('header-nav__menu-subsubmenu')) {
          e.stopPropagation();
          const activatorId = submenu.getAttribute('id');
          const activator = document.querySelector('.js-header-nav-submenu-activate[aria-controls="' + activatorId + '"]');
          if (activator) {
            this.ignoreFocusOpen = true;
            activator.setAttribute('aria-expanded', 'false');
            activator.parentElement.classList.remove('is-focused');
            submenu.setAttribute('aria-hidden', 'true');
            activator.focus();
          }
        } else {
          let activator;
          if (this.activeSubmenu) {
            activator = document.querySelector(
              '.js-header-nav-submenu-activate[data-submenu-id="' + this.activeSubmenu + '"]'
            );
          } else {
            const focusedParent2 = document.querySelector('.header-nav__menu-item.is-focused');
            if (focusedParent2) activator = focusedParent2.querySelector('.js-header-nav-submenu-activate');
          }

          this.closeActiveSubmenu();

          if (activator) {
            this.ignoreFocusOpen = true;
            activator.focus();
          }
        }
      }
    }

    onGlobalKeyDown(e) {
      if (e.key === 'Escape' && this.activeSubmenu) {
        this.closeActiveSubmenu();
        const activator = document.querySelector(
          '.js-header-nav-submenu-activate[data-submenu-id="' + this.activeSubmenu + '"]'
        );
        if (activator) activator.focus();
      }
    }

    onActivatorKeyDown(e) {
      if ((e.key === ' ' || e.key === 'Spacebar') && !e.shiftKey) {
        e.preventDefault();

        const activator = e.currentTarget;
        const relativeSubmenuId = activator.dataset.submenuId;

        let isOpen = false;
        if (relativeSubmenuId) {
          isOpen = this.activeSubmenu === relativeSubmenuId;
        } else if (activator.classList.contains('header-nav__menu-sublink')) {
          isOpen = activator.getAttribute('aria-expanded') === 'true';
        }

        if (isOpen) {
          if (relativeSubmenuId) {
            this.closeActiveSubmenu();
            activator.focus();
          } else {
            this.ignoreFocusOpen = true;
            activator.setAttribute('aria-expanded', 'false');
            activator.parentElement.classList.remove('is-focused');
            const subSubmenu = activator.parentElement.querySelector('.header-nav__menu-subsubmenu');
            if (subSubmenu) subSubmenu.setAttribute('aria-hidden', 'true');
            activator.focus();
          }
        } else {
          this.onSubmenuEnter(e);
        }
        return;
      }

      if (e.key === 'Tab' && !e.shiftKey) {
        const activator = e.currentTarget;
        let activeSubmenuEl;

        if (this.activeSubmenu && activator.dataset.submenuId === this.activeSubmenu) {
          activeSubmenuEl = document.querySelector('.js-header-nav-submenu[data-submenu-id="' + this.activeSubmenu + '"]');
        } else if (activator.classList.contains('header-nav__menu-sublink')) {
          if (activator.getAttribute('aria-expanded') === 'true') {
            activeSubmenuEl = activator.parentElement.querySelector('.header-nav__menu-subsubmenu');
          }
        } else {
          const parentItem = activator.closest('.header-nav__menu-item');
          if (parentItem && parentItem.classList.contains('is-focused')) {
            activeSubmenuEl = parentItem.querySelector('.header-nav__menu-submenu');
          }
        }

        if (activeSubmenuEl) {
          const firstFocusable = activeSubmenuEl.querySelector(
            'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
          );
          if (firstFocusable) {
            e.preventDefault();
            firstFocusable.focus();
          }
        }
      }
    }

    closeActiveSubmenu(exceptDetails) {
      const openClickDetails = this.DOM.headerNav.querySelectorAll(
        '.header-nav__menu-details[data-open-trigger="click"][open]'
      );
      openClickDetails.forEach((details) => {
        if (details === exceptDetails || (exceptDetails && exceptDetails.contains(details))) return;
        details.open = false;
      });

      if (this.activeDetails) {
        this.activeDetails.querySelectorAll('details').forEach((nested) => {
          nested.open = false;
        });
        this.activeDetails.open = false;
        this.activeDetails = null;
      }

      if (this.activeSubmenu || document.querySelector('.header-nav__menu-item.is-focused')) {
        document.body.classList.remove('is-submenu-open');

        if (this.activeSubmenu) {
          const oldSubmenu = document.querySelector('.js-header-nav-submenu[data-submenu-id="' + this.activeSubmenu + '"]');
          if (oldSubmenu) {
            this.ctx.add(() => {
              gsap.to(oldSubmenu, {
                y: 0,
                opacity: 0,
                pointerEvents: 'none',
                yPercent: -100,
                duration: 0.5,
                ease: easePower4,
                onComplete: () => {
                  gsap.set(oldSubmenu, { visibility: 'hidden' });
                },
              });
            });
            oldSubmenu.setAttribute('aria-hidden', 'true');

            const openShopToggles = oldSubmenu.querySelectorAll('.js-header-submenu-shop-toggle[aria-expanded="true"]');
            openShopToggles.forEach((toggle) => {
              toggle.setAttribute('aria-expanded', 'false');
              const sublist = oldSubmenu.querySelector(
                '.js-header-submenu-shop-sublist[data-sublist-id="' + toggle.dataset.sublistId + '"]'
              );
              if (sublist) {
                sublist.setAttribute('aria-hidden', 'true');
                sublist.removeAttribute('style');
              }
            });
          }
          const oldActivator = document.querySelector(
            '.js-header-nav-submenu-activate[data-submenu-id="' + this.activeSubmenu + '"]'
          );
          if (oldActivator) oldActivator.setAttribute('aria-expanded', 'false');
          this.activeSubmenu = undefined;
        }

        const focusedParent = document.querySelector('.header-nav__menu-item.is-focused');
        if (focusedParent) {
          const openNestedActivators = focusedParent.querySelectorAll('.header-nav__menu-sublink[aria-expanded="true"]');
          openNestedActivators.forEach((activator) => {
            activator.setAttribute('aria-expanded', 'false');
            activator.parentElement.classList.remove('is-focused');
            const subSubmenu = activator.parentElement.querySelector('.header-nav__menu-subsubmenu');
            if (subSubmenu) subSubmenu.setAttribute('aria-hidden', 'true');
          });

          focusedParent.classList.remove('is-focused');
        }
      }
    }

    onSubmenuEnter(e) {
      if (this.ignoreFocusOpen && e.type === 'focus') {
        this.ignoreFocusOpen = false;
        return;
      }
      const target = e.currentTarget;
      const relativeSubmenuId = target.dataset.submenuId;

      if (relativeSubmenuId) {
        if (this.activeSubmenu === relativeSubmenuId) return;

        this.closeActiveSubmenu();

        if (this.isOpenSearch) {
          this.closeSearch();
        }

        const newSubmenu = document.querySelector('.js-header-nav-submenu[data-submenu-id="' + relativeSubmenuId + '"]');
        if (newSubmenu) {
          document.body.classList.add('is-submenu-open');
          this.ctx.add(() => {
            gsap.to(newSubmenu, {
              onStart: () => {
                gsap.set(newSubmenu, { visibility: 'visible' });
                this.activeSubmenu = relativeSubmenuId;
                target.setAttribute('aria-expanded', 'true');
              },
              y: 0,
              opacity: 1,
              pointerEvents: 'all',
              yPercent: 0,
              duration: 0.5,
              ease: easePower4,
            });
          });
          newSubmenu.setAttribute('aria-hidden', 'false');
        }
      } else {
        if (target.classList.contains('header-nav__menu-sublink')) {
          const parentSubLinkList = target.closest('.header-nav__menu-sublinks');
          if (parentSubLinkList) {
            const openSiblings = parentSubLinkList.querySelectorAll('.header-nav__menu-sublink[aria-expanded="true"]');
            openSiblings.forEach((sibling) => {
              if (sibling !== target) {
                sibling.setAttribute('aria-expanded', 'false');
                sibling.parentElement.classList.remove('is-focused');
                const siblingSub = sibling.parentElement.querySelector('.header-nav__menu-subsubmenu');
                if (siblingSub) siblingSub.setAttribute('aria-hidden', 'true');
              }
            });
          }

          target.setAttribute('aria-expanded', 'true');
          target.parentElement.classList.add('is-focused');
          const subSubmenu = target.parentElement.querySelector('.header-nav__menu-subsubmenu');
          if (subSubmenu) {
            subSubmenu.setAttribute('aria-hidden', 'false');
            this.flipSubsubmenu(subSubmenu);
          }
        } else {
          this.closeActiveSubmenu();

          if (this.isOpenSearch) {
            this.closeSearch();
          }

          const parentItem = target.closest('.header-nav__menu-item');
          if (parentItem && parentItem.querySelector('.header-nav__menu-submenu')) {
            parentItem.classList.add('is-focused');
          }
        }
      }
    }

    onOtherSubLinkEnter() {
      this.closeActiveSubmenu();
    }

    // Flip the subsubmenu to the left side when it would overflow the right edge of the viewport.
    flipSubsubmenu(subsubmenu) {
      if (!subsubmenu) return;
      // Reset to the default (right) side before measuring so the decision is idempotent.
      subsubmenu.classList.remove('header-nav__menu-subsubmenu--flip-left');
      const rect = subsubmenu.getBoundingClientRect();
      const margin = 16;
      if (rect.right > window.innerWidth - margin) {
        subsubmenu.classList.add('header-nav__menu-subsubmenu--flip-left');
      }
    }

    onDocumentMouseMove(e) {
      const target = e.target;

      if (this.activeSubmenu) {
        const activator = document.querySelector(
          '.js-header-nav-submenu-activate[data-submenu-id="' + this.activeSubmenu + '"]'
        );
        const isClickTriggered = activator && activator.dataset.openTrigger === 'click';

        if (!isClickTriggered) {
          const isOutsideNav = !this.DOM.headerNav.contains(target);
          const isOutsideSubmenu = !Array.from(this.DOM.submenus).some((submenu) => submenu.contains(target));

          if (isOutsideNav && isOutsideSubmenu) {
            this.closeActiveSubmenu();
          }
        }
      }

      if (this.activeDetails) {
        if (!this.DOM.headerNav.contains(target)) {
          this.closeActiveSubmenu();
        }
      }

      const focusedMenuItem = document.querySelector('.header-nav__menu-item.is-focused');
      if (focusedMenuItem) {
        if (!focusedMenuItem.contains(target)) {
          this.closeActiveSubmenu();
        }
      }
    }

    onSubExploreLinkEnter(e) {
      e.currentTarget.classList.add('is-hover');
      this.DOM.submenuExplore.classList.add('is-hover-link');
    }

    onSubExploreLinkLeave(e) {
      e.currentTarget.classList.remove('is-hover');
      this.DOM.submenuExplore.classList.remove('is-hover-link');
    }

    onMenuExploreLinkEnter(e) {
      e.currentTarget.classList.add('is-hover');
      if (this.DOM.menuExploreWrap) {
        this.DOM.menuExploreWrap.classList.add('is-hover-link');
      }
    }

    onMenuExploreLinkLeave(e) {
      e.currentTarget.classList.remove('is-hover');
      if (this.DOM.menuExploreWrap) {
        this.DOM.menuExploreWrap.classList.remove('is-hover-link');
      }
    }

    onMenuToggle() {
      if (this.isOpenMenu) {
        this.closeMenu();
      } else {
        this.openMenu();
      }
    }

    onCloseMenu() {
      this.closeMenu();
    }

    openMenu() {
      if (this.isOpenSearch) {
        this.closeSearch();
      }

      if (!this.isAnimatingMenu) {
        this.isAnimatingMenu = true;

        document.body.classList.add('is-menu-open');

        const headerMainDesk = this.DOM.headerNav.querySelector('.header-nav__main--desktop');
        const headerMainMobile = this.DOM.headerNav.querySelector('.header-nav__main--mobile');

        if (this.isTransparent) {
          if (headerMainDesk) headerMainDesk.setAttribute('is-solid', true);
          if (headerMainMobile) headerMainMobile.setAttribute('is-solid', true);
        }

        this.DOM.menuWrap.setAttribute('aria-hidden', 'false');
        if (this.DOM.headerNavMenuToggle) {
          this.DOM.headerNavMenuToggle.setAttribute('aria-expanded', 'true');
        }

        this.ctx.add(() => {
          const openTl = gsap.timeline({
            onComplete: () => {
              this.isAnimatingMenu = false;
              this.isOpenMenu = true;
            },
          });

          openTl
            .set(this.DOM.menuWrap, { pointerEvents: 'all', opacity: 1, zIndex: 49 })
            .set(this.DOM.menuClose, { display: 'block' })
            .to(this.DOM.menuInner, {
              xPercent: 0,
              opacity: 1,
              duration: 0.9,
              ease: easePower4,
              onComplete: () => {
                if (this.DOM.menuInner) {
                  const focusableElements = this.DOM.menuInner.querySelectorAll(
                    'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
                  );
                  const visibleFocusableElements = Array.from(focusableElements).filter((el) => {
                    const style = window.getComputedStyle(el);
                    return style.display !== 'none' && style.visibility !== 'hidden' && el.offsetParent !== null;
                  });
                  if (visibleFocusableElements.length > 0) {
                    visibleFocusableElements[0].focus();
                  }
                }
              },
            });
        });
      }
    }

    closeMenu(immediate = false) {
      if (!this.isAnimatingMenu) {
        this.isAnimatingMenu = true;

        document.body.classList.remove('is-menu-open');

        const headerMainDesk = this.DOM.headerNav.querySelector('.header-nav__main--desktop');
        const headerMainMobile = this.DOM.headerNav.querySelector('.header-nav__main--mobile');

        if (this.isTransparent) {
          if (headerMainDesk) headerMainDesk.removeAttribute('is-solid');
          if (headerMainMobile) headerMainMobile.removeAttribute('is-solid');
        }

        this.DOM.menuWrap.setAttribute('aria-hidden', 'true');
        if (this.DOM.headerNavMenuToggle) {
          this.DOM.headerNavMenuToggle.setAttribute('aria-expanded', 'false');
        }

        if (this.activeMenuSide) {
          const relativeSideEl = this.DOM.menuWrap.querySelector(
            '.js-header-nav-menu-side[data-sub-id="' + this.activeMenuSide + '"]'
          );
          this.closeSide(relativeSideEl);
        }

        if (this.activeMenuSub) {
          const relativeSub = this.DOM.menuWrap.querySelector(
            '.js-header-nav-menu-submenu[data-sub-id="' + this.activeMenuSub + '"]'
          );
          this.closeSub(relativeSub);
        }

        this.ctx.add(() => {
          const closeTl = gsap.timeline({
            onComplete: () => {
              this.isAnimatingMenu = false;
              this.isOpenMenu = false;
            },
          });

          closeTl
            .set(this.DOM.menuClose, { display: 'none' })
            .to(this.DOM.menuInner, {
              xPercent: -100,
              opacity: 0,
              duration: animProp(0.9, immediate),
              ease: easePower4,
            })
            .set(this.DOM.menuWrap, { pointerEvents: 'none', opacity: 0, zIndex: -1 });
        });
      }
    }

    onMenuLinkSideClick(e) {
      const activator = e.currentTarget;
      const newrelativeSideId = activator.dataset.subId;

      if (this.activeMenuSub) {
        const relativeSub = this.DOM.menuWrap.querySelector(
          '.js-header-nav-menu-submenu[data-sub-id="' + this.activeMenuSub + '"]'
        );
        if (relativeSub) {
          this.closeSub(relativeSub);
        }
      }

      const newRelativeSideEl = this.DOM.menuWrap.querySelector(
        '.js-header-nav-menu-side[data-sub-id="' + newrelativeSideId + '"]'
      );

      if (!this.activeMenuSide) {
        this.openSide(newRelativeSideEl);
        activator.setAttribute('aria-expanded', 'true');
      } else if (this.activeMenuSide) {
        const oldSideEl = this.DOM.menuWrap.querySelector(
          '.js-header-nav-menu-side[data-sub-id="' + this.activeMenuSide + '"]'
        );

        if (this.activeMenuSide !== newrelativeSideId) {
          this.closeSide(oldSideEl);
          this.openSide(newRelativeSideEl);
          activator.setAttribute('aria-expanded', 'true');
        } else {
          this.closeSide(oldSideEl);
        }
      }
    }

    onMenuLinkSubClick(e) {
      const activator = e.currentTarget;
      const relativeSubId = activator.dataset.subId;

      if (this.activeMenuSide) {
        const relativeSideEl = this.DOM.menuWrap.querySelector(
          '.js-header-nav-menu-side[data-sub-id="' + this.activeMenuSide + '"]'
        );
        this.closeSide(relativeSideEl);
      }

      if (this.activeMenuSubsub) {
        const oldSubEl = this.DOM.menuWrap.querySelector(
          '.js-header-nav-menu-subsubmenu[data-sub-id="' + this.activeMenuSubsub + '"]'
        );
        this.closeSubsub(oldSubEl);
      }

      const newRelativeSubEl = this.DOM.menuWrap.querySelector(
        '.js-header-nav-menu-submenu[data-sub-id="' + relativeSubId + '"]'
      );

      if (!this.activeMenuSub) {
        this.openSub(newRelativeSubEl);
        activator.setAttribute('aria-expanded', 'true');
      } else if (this.activeMenuSub) {
        const oldSubEl2 = this.DOM.menuWrap.querySelector(
          '.js-header-nav-menu-submenu[data-sub-id="' + this.activeMenuSub + '"]'
        );

        if (this.activeMenuSub !== relativeSubId) {
          this.closeSub(oldSubEl2);
          this.openSub(newRelativeSubEl);
          activator.setAttribute('aria-expanded', 'true');
        } else {
          this.closeSub(oldSubEl2);
        }
      }
    }

    onMenuSubLinkSubClick(e) {
      const activator = e.currentTarget;
      const relativeSubId = activator.dataset.subId;

      if (this.activeMenuSide) {
        const relativeSideEl = this.DOM.menuWrap.querySelector(
          '.js-header-nav-menu-side[data-sub-id="' + this.activeMenuSide + '"]'
        );
        this.closeSide(relativeSideEl);
      }

      const newRelativeSubEl = this.DOM.menuWrap.querySelector(
        '.js-header-nav-menu-subsubmenu[data-sub-id="' + relativeSubId + '"]'
      );

      if (!this.activeMenuSubsub) {
        this.openSubsub(newRelativeSubEl);
        activator.setAttribute('aria-expanded', 'true');
      } else if (this.activeMenuSubsub) {
        const oldSubEl = this.DOM.menuWrap.querySelector(
          '.js-header-nav-menu-subsubmenu[data-sub-id="' + this.activeMenuSubsub + '"]'
        );

        if (this.activeMenuSubsub !== relativeSubId) {
          this.closeSubsub(oldSubEl);
          this.openSubsub(newRelativeSubEl);
          activator.setAttribute('aria-expanded', 'true');
        } else {
          this.closeSubsub(oldSubEl);
        }
      }
    }

    onMenuShopSublistToggle(e) {
      const activator = e.currentTarget;
      const sublistId = activator.dataset.sublistId;
      const sublist = this.DOM.menuWrap.querySelector('.js-header-menu-shop-sublist[data-sublist-id="' + sublistId + '"]');

      if (!sublist) return;

      const isOpen = activator.getAttribute('aria-expanded') === 'true';

      if (isOpen) {
        activator.setAttribute('aria-expanded', 'false');
        sublist.setAttribute('aria-hidden', 'true');
        this.ctx.add(() => {
          gsap.to(sublist, { height: 0, autoAlpha: 0, duration: 0.4, ease: easePower4 });
        });
      } else {
        const parentGroup = activator.closest('.header-menu__side-shop-group');
        if (parentGroup) {
          const openToggles = parentGroup.querySelectorAll('.js-header-menu-shop-toggle[aria-expanded="true"]');
          openToggles.forEach((toggle) => {
            if (toggle !== activator) {
              const otherId = toggle.dataset.sublistId;
              const otherSublist = this.DOM.menuWrap.querySelector(
                '.js-header-menu-shop-sublist[data-sublist-id="' + otherId + '"]'
              );
              toggle.setAttribute('aria-expanded', 'false');
              if (otherSublist) {
                otherSublist.setAttribute('aria-hidden', 'true');
                this.ctx.add(() => {
                  gsap.to(otherSublist, { height: 0, autoAlpha: 0, duration: 0.4, ease: easePower4 });
                });
              }
            }
          });
        }

        activator.setAttribute('aria-expanded', 'true');
        sublist.setAttribute('aria-hidden', 'false');
        this.ctx.add(() => {
          gsap.to(sublist, { height: 'auto', autoAlpha: 1, duration: 0.4, ease: easePower4 });
        });
      }
    }

    openSide(sideEl) {
      this.activeMenuSide = sideEl.dataset.subId;

      this.DOM.menuWrap.classList.add('is-menu-side-open');
      sideEl.setAttribute('aria-hidden', 'false');

      this.ctx.add(() => {
        gsap.to(sideEl, {
          xPercent: 0,
          opacity: 1,
          pointerEvents: 'all',
          duration: 0.9,
          ease: easePower4,
        });
      });
    }

    closeSide(sideEl) {
      this.activeMenuSide = undefined;

      this.DOM.menuWrap.classList.remove('is-menu-side-open');
      sideEl.setAttribute('aria-hidden', 'true');

      const activator = this.DOM.menuWrap.querySelector(
        '.js-header-nav-menu-has-side[data-sub-id="' + sideEl.dataset.subId + '"]'
      );
      if (activator) {
        activator.setAttribute('aria-expanded', 'false');
      }

      this.ctx.add(() => {
        gsap.to(sideEl, {
          xPercent: -100,
          opacity: 0,
          pointerEvents: 'none',
          duration: 0.9,
          ease: easePower4,
        });
      });
    }

    openSub(subEl) {
      this.activeMenuSub = subEl.dataset.subId;

      subEl.setAttribute('aria-hidden', 'false');

      this.ctx.add(() => {
        gsap.to(subEl, {
          height: 'auto',
          autoAlpha: 1,
          duration: 0.5,
          ease: easePower4,
        });
      });
    }

    closeSub(subEl) {
      this.activeMenuSub = undefined;

      subEl.setAttribute('aria-hidden', 'true');

      const activator = this.DOM.menuWrap.querySelector(
        '.js-header-nav-menu-has-sub[data-sub-id="' + subEl.dataset.subId + '"]'
      );
      if (activator) {
        activator.setAttribute('aria-expanded', 'false');
      }

      this.ctx.add(() => {
        gsap.to(subEl, {
          height: 0,
          autoAlpha: 0,
          duration: 0.5,
          ease: easePower4,
        });
      });
    }

    openSubsub(subEl) {
      this.activeMenuSubsub = subEl.dataset.subId;

      subEl.setAttribute('aria-hidden', 'false');

      this.ctx.add(() => {
        gsap.to(subEl, {
          height: 'auto',
          autoAlpha: 1,
          duration: 0.5,
          ease: easePower4,
        });
      });
    }

    closeSubsub(subEl) {
      this.activeMenuSubsub = undefined;

      subEl.setAttribute('aria-hidden', 'true');

      const activator = this.DOM.menuWrap.querySelector(
        '.js-header-nav-menu-has-subsub[data-sub-id="' + subEl.dataset.subId + '"]'
      );
      if (activator) {
        activator.setAttribute('aria-expanded', 'false');
      }

      this.ctx.add(() => {
        gsap.to(subEl, {
          height: 0,
          autoAlpha: 0,
          duration: 0.5,
          ease: easePower4,
        });
      });
    }

    toggleSearch() {
      if (this.isOpenSearch) {
        this.closeSearch();
      } else {
        this.openSearch();
      }
    }

    onCloseSearch() {
      this.closeSearch();
    }

    closeSearch(immediate = false) {
      if (!this.isAnimatingSearch) {
        this.isAnimatingSearch = true;

        document.body.classList.remove('is-search-open');

        this.DOM.searchLayer.setAttribute('aria-hidden', 'true');

        document.removeEventListener('keydown', this.onKeyDownSearch);

        this.ctx.add(() => {
          const closeTl = gsap.timeline({
            onStart: () => {
              this.DOM.searchInput.blur();
            },
            onComplete: () => {
              gsap.set(this.DOM.searchLayer, { zIndex: -1 });

              this.isAnimatingSearch = false;
              this.isOpenSearch = false;

              this.removeTrapFocus();

              if (this.DOM.searchActivators && this.DOM.searchActivators.length > 0) {
                this.DOM.searchActivators.forEach((btn) => { btn.setAttribute('aria-expanded', 'false'); });
                this.DOM.searchActivators[0].focus();
              }
            },
          });

          closeTl.to(this.DOM.searchCloser, { pointerEvents: 'none', opacity: 0, zIndex: -1 }, 0);
          closeTl.to(
            this.DOM.searchLayer,
            {
              yPercent: -100,
              autoAlpha: 0,
              pointerEvents: 'none',
              duration: animProp(0.9, immediate || prefersReducedMotion()),
              ease: easePower4,
            },
            0
          );
        });
      }
    }

    openSearch() {
      if (this.isOpenMenu) {
        this.closeMenu();
      }

      if (this.mobileMenu && this.mobileMenu.isOpenMenu) {
        this.mobileMenu.closeMenu();
      }

      if (!this.isAnimatingSearch) {
        this.isAnimatingSearch = true;

        document.body.classList.add('is-search-open');

        this.DOM.searchLayer.setAttribute('aria-hidden', 'false');

        this.ctx.add(() => {
          const openTl = gsap.timeline({
            onStart: () => {
              gsap.set(this.DOM.searchLayer, { zIndex: 49 });
            },
            onComplete: () => {
              this.isAnimatingSearch = false;
              this.isOpenSearch = true;

              this.DOM.searchInput.focus();
              this.trapFocus(this.DOM.searchLayer);

              if (this.DOM.searchActivators) {
                this.DOM.searchActivators.forEach((btn) => { btn.setAttribute('aria-expanded', 'true'); });
              }

              document.addEventListener('keydown', this.onKeyDownSearch);
            },
          });

          openTl.to(this.DOM.searchCloser, { pointerEvents: 'all', opacity: 1, zIndex: 48 }, 0);
          openTl.to(
            this.DOM.searchLayer,
            {
              y: 0,
              yPercent: 0,
              autoAlpha: 1,
              pointerEvents: 'all',
              duration: animProp(0.9, prefersReducedMotion()),
              ease: easePower4,
            },
            0
          );
        });
      }
    }

    destroy() {
      if (this.DOM) {
        document.body.classList.remove('is-submenu-open', 'is-menu-open', 'is-search-open');

        document.removeEventListener('keydown', this.onKeyDownSearch);
        this.removeTrapFocus();

        if (this.ctx) {
          this.ctx.revert();
          this.ctx = null;
        }

        this.destroyHeaderObservers();

        if (this.mobileMenu) {
          if (this.mobileMenuUpdateUnsubscriber) this.mobileMenuUpdateUnsubscriber();
          this.mobileMenu.destroy();
          this.mobileMenu = undefined;
        }

        if (this.searchInstance) {
          this.searchInstance.destroy();
          this.searchInstance = undefined;
        }

        this.DOM = undefined;
      }
    }

    trapFocus(element) {
      this.trapElement = element;
      this.trapElement.addEventListener('keydown', this.onTrapFocusKeydown);
    }

    removeTrapFocus() {
      if (this.trapElement) {
        this.trapElement.removeEventListener('keydown', this.onTrapFocusKeydown);
        this.trapElement = null;
      }
    }

    onTrapFocusKeydown(e) {
      const isTabPressed = e.key === 'Tab' || e.keyCode === 9;

      if (!isTabPressed) {
        return;
      }

      const focusableElements = this.trapElement.querySelectorAll(
        'button:not([disabled]):not([tabindex="-1"]), [href]:not([tabindex="-1"]), input:not([disabled]):not([tabindex="-1"]), select:not([disabled]):not([tabindex="-1"]), textarea:not([disabled]):not([tabindex="-1"]), [tabindex]:not([tabindex="-1"])'
      );
      const firstFocusableElement = focusableElements[0];
      const lastFocusableElement = focusableElements[focusableElements.length - 1];

      if (e.shiftKey) {
        if (document.activeElement === firstFocusableElement) {
          lastFocusableElement.focus();
          e.preventDefault();
        }
      } else {
        if (document.activeElement === lastFocusableElement) {
          firstFocusableElement.focus();
          e.preventDefault();
        }
      }
    }

    disableStickyFilters() {
      const collectionFilters = document.querySelectorAll('.collection-grid__product-filters');
      collectionFilters.forEach((filter) => {
        filter.classList.add('disable-sticky');
      });
    }

    init(container = document) {
      const headerNav = container.querySelector('.js-header-nav');

      if (headerNav) {
        this.DOM = {};
        this.DOM.main = document.querySelector('.l-main');
        this.DOM.headerNav = headerNav;
        this.DOM.headerNavSubActivators = this.DOM.headerNav.querySelectorAll('.js-header-nav-submenu-activate');
        this.DOM.headerOtherItems = this.DOM.headerNav.querySelectorAll('.js-header-nav-submenu-item');
        this.DOM.headerNavMenuToggle = this.DOM.headerNav.querySelector('.js-header-nav-menu-toggle');

        this.DOM.menuWrap = container.querySelector('.js-header-nav-menu');

        this.DOM.submenus = container.querySelectorAll('.js-header-nav-submenu');
        this.DOM.classicSubmenus = container.querySelectorAll('.header-nav__menu-submenu, .header-nav__menu-subsubmenu');
        this.DOM.submenuExploreLinks = container.querySelectorAll('.js-header-submenu-explore-link');
        this.DOM.submenuCollections = container.querySelectorAll('.js-header-nav-submenu-collection');

        this.DOM.mobileToggles = container.querySelectorAll('.js-header-mobile-toggle');

        this.DOM.searchActivators = container.querySelectorAll('.js-gsearch-toggle');
        this.DOM.searchCloser = container.querySelector('.js-gsearch-close');
        this.DOM.searchLayer = container.querySelector('.js-gsearch');

        this.ctx = gsap.context(() => {}, this.DOM.headerNav);

        this.onSubmenuKeyDown = this.onSubmenuKeyDown.bind(this);
        this.onSubmenuEnter = this.onSubmenuEnter.bind(this);
        this.onActivatorKeyDown = this.onActivatorKeyDown.bind(this);
        this.onDocumentMouseMove = this.onDocumentMouseMove.bind(this);
        this.onGlobalKeyDown = this.onGlobalKeyDown.bind(this);
        this.onGlobalFocusIn = this.onGlobalFocusIn.bind(this);
        this.onOtherSubLinkEnter = this.onOtherSubLinkEnter.bind(this);
        this.onSubExploreLinkEnter = this.onSubExploreLinkEnter.bind(this);
        this.onSubExploreLinkLeave = this.onSubExploreLinkLeave.bind(this);
        this.onSubCollectionsEnter = () => {};
        this.onSubCollectionsLeave = () => {};
        this.onShopSublistToggle = this.onShopSublistToggle.bind(this);
        this.toggleSearch = this.toggleSearch.bind(this);
        this.onCloseSearch = this.onCloseSearch.bind(this);
        this.onKeyDownSearch = this.onKeyDownSearch.bind(this);
        this.onKeyDownMenu = this.onKeyDownMenu.bind(this);
        this.onMenuToggle = this.onMenuToggle.bind(this);
        this.onCloseMenu = this.onCloseMenu.bind(this);
        this.onMenuLinkSideClick = this.onMenuLinkSideClick.bind(this);
        this.onMenuLinkSubClick = this.onMenuLinkSubClick.bind(this);
        this.onMenuSubLinkSubClick = this.onMenuSubLinkSubClick.bind(this);
        this.onMenuShopSublistToggle = this.onMenuShopSublistToggle.bind(this);
        this.onMenuExploreLinkEnter = this.onMenuExploreLinkEnter.bind(this);
        this.onMenuExploreLinkLeave = this.onMenuExploreLinkLeave.bind(this);
        this.onMobileMenuOpen = this.onMobileMenuOpen.bind(this);
        this.onTrapFocusKeydown = this.onTrapFocusKeydown.bind(this);

        this.mobileMenu = undefined;
        this.activeSubmenu = undefined;
        this.activeMenuSide = undefined;
        this.activeMenuSub = undefined;
        this.activeMenuSubsub = undefined;
        this.searchInstance = undefined;
        this.isFloatingHeader = this.DOM.headerNav.classList.contains('is-floating-header');
        this.isTransparent = this.DOM.headerNav.classList.contains('is-transparent');

        if (this.isFloatingHeader) {
          this.disableStickyFilters();
        }

        this.isAnimatingMenu = false;
        this.isAnimatingSearch = false;
        this.isOpenMenu = false;
        this.isOpenSearch = false;
        this.ignoreFocusOpen = false;

        this.setup();
      }
    }
  }

  window.Universe.Navigation = Navigation;
})();
